import { HTTP_STATUS } from "@api/constant";
import { BrightcoveVideo } from "@blocks/BrightcoveVideo/BrightcoveVideo";
import BmCard from "@blocks/Card/BmCard";
import { BmSectionName } from "@config/SectionConstants";
import BmMainContainer from "@container/BmMainContainer";
import WrapperBlock from "@container/WrapperBlock";
import ThumbnailCard from "@elements/Card/Thumbnail/ThumbnailCard";
import Heading from "@elements/Typography/Heading";
import { BC_BM_PLAYER_ID } from "@helper/getEnvVariables";
import { GADataProps, getGTMData } from "@helper/getGtmData";
import { LatestVideoResponse } from "@helper/transformer/useBrightcoveResponse";
import { useMobWebView } from "@hooks/useMobWebView";
import { Route } from "@route-types/BalarMurasu/+types/BalarMurasu";
import ErrorPage from "@routes/ErrorPage";
import NotFound from "@routes/NotFound";
import {
  fetchContext,
  ResponseType,
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { createMetaTags, MetaTagsProps } from "@sphtech/web2-seo-schema";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { BmPayloadResponseType } from "@typings/PagePayloadType";
import { Fragment } from "react";
import { ReactElement, useRef, useState } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";
import { twMerge } from "tailwind-merge";

import { fetchBMPageData } from "./BalarMurasu.server";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    BmPayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function BalarMurasu(): ReactElement {
  const dataLoaderResponse: TRouteWithoutRedirect<
    BmPayloadResponseType,
    string
  > = useLoaderData();

  const mobwebviewBool = useMobWebView();

  const containerRef = useRef<HTMLDivElement>(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }
  const bmIssues = dataLoaderResponse.payload.issues;
  const [firstIssue, ...archieves] = bmIssues;

  const videosList = dataLoaderResponse.payload.videos;

  const handleOnEndedVideo = () => {
    const index = currentVideoIndex + 1;
    if (index === videosList.length - 1) {
      setCurrentVideoIndex(0);
    } else {
      setCurrentVideoIndex(index);
    }
  };

  const handleOnClickThumbnail = (index: number) => () => {
    setCurrentVideoIndex(index);
    containerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  return (
    <BmMainContainer
      sectionName={BmSectionName}
      mobwebview={mobwebviewBool}
      pageAdTargetValue={pageAdTargetValue}
    >
      <WrapperBlock className="">
        <div className="border-b border-solid border-b-white-100 pb-2 mb-3">
          <div className="flex flex-wrap gap-1">
            <div className="flex-[59%] text-left overflow-hidden max-lg:flex-100">
              <Heading
                Element="h3"
                className="text-pink-600 text-3md font-bold leading-10 pb-2 mb-2 max-lg:text-2md max-md:text-sm"
              >
                காணொளிகள்
              </Heading>
              {videosList[currentVideoIndex]?.id && (
                <BrightcoveVideo
                  videoId={videosList[currentVideoIndex].id}
                  forwardedRef={containerRef}
                  onEnded={handleOnEndedVideo}
                  nextPlaylist={handleOnEndedVideo}
                  playerId={BC_BM_PLAYER_ID}
                />
              )}

              <div className="bg-gray-150 lg:mt-1 p-2 pr-0">
                <div
                  id="scroll-container"
                  className={twMerge(
                    "flex overflow-y-hidden overflow-x-scroll gap-2.5",
                  )}
                >
                  {videosList.map((video, index) => (
                    <ThumbnailCard<LatestVideoResponse>
                      key={index}
                      handleOnClickThumbnail={handleOnClickThumbnail(index)}
                      variant="video"
                      {...video}
                      nowPlaying={currentVideoIndex === index}
                      customCardStyles="flex-col w-50 h-49 lg:py-1 max-sm:w-31.75 max-sm:h-34"
                      isDark
                      videoPosterDimensions="h-31.5 w-55 max-sm:h-31.5 max-sm:w-31.75"
                      isHideDateTime
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex-[40%] text-center">
              <BmCard {...firstIssue} showHeadline={true} />
            </div>
          </div>
        </div>

        <Heading
          Element="h3"
          className="text-pink-600 text-2md max-lg:text-2md max-md:text-sm font-bold leading-6 mb-3 mt-3 py-2"
        >
          மற்ற பதிப்புகளைக் காண்க
        </Heading>
        <div className="flex flex-wrap gap-3 justify-normal max-lg:justify-center">
          {[...archieves].map((issue) => (
            <Fragment key={issue.id}>
              <BmCard {...issue} />
            </Fragment>
          ))}
        </div>
      </WrapperBlock>
    </BmMainContainer>
  );
}

export async function loader() {
  const data = await fetchBMPageData();

  if (data.statusCode !== HTTP_STATUS.SUCCESS) return { ...data };
  const gaData = data.payload.gaData;

  return {
    ...data,
    gtm: getGTMData(gaData as GADataProps),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<BmPayloadResponseType, string>
  >;
}
